import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { C, FilterSelect, Panel } from '@/design-system/dna';
import { ClassMultiSelect } from '@/pages/AcademicoRelatorios/reporting/ClassMultiSelect';
import { brl, fmtDate } from '../shared';
import { AuthBadge, ParticipantShell, PayBadge, TableCell, TableRow, useParticipantFilter } from '../ParticipantTable';

const ALL = 'all';

function ClassPicker({ data, setData, eventId }) {
  const thisYear = data.event.date_iso ? Number(data.event.date_iso.slice(0, 4)) : new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [opts, setOpts] = useState([]);
  const [cycle, setCycle] = useState(ALL);
  const [seg, setSeg] = useState(ALL);
  const [sel, setSel] = useState(data.event.class_ids || []);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get(`/eventos/${eventId}/turmas-disponiveis`, { params: { year } }).then(({ data: d }) => setOpts(d.classes)).catch(() => setOpts([])); }, [eventId, year]);
  const cycles = [...new Set(opts.map((c) => c.cycle).filter(Boolean))];
  const segs = [...new Set(opts.map((c) => c.segment).filter(Boolean))];
  const shown = useMemo(() => opts.filter((c) => (cycle === ALL || c.cycle === cycle) && (seg === ALL || c.segment === seg)), [opts, cycle, seg]);
  const save = async () => {
    setSaving(true);
    try { const { data: d } = await api.put(`/eventos/${eventId}/turmas`, { class_ids: sel }); setData(d); toast.success(`Turmas salvas · ${d.added} aluno(s) incluído(s), ${d.removed} removido(s).`); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Falha ao salvar turmas.'); } finally { setSaving(false); }
  };
  return (
    <Panel title="Turmas participantes" kicker="Seleção" testid="evento-turmas" right={<Button onClick={save} disabled={saving} data-testid="evento-turmas-salvar">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Carregar alunos</Button>}>
      <div className="flex flex-wrap items-start gap-2">
        <FilterSelect id="evento-turmas-ano" value={year} onChange={(v) => setYear(Number(v))} items={[thisYear - 1, thisYear, thisYear + 1].map((y) => ({ value: y, label: `Ano ${y}` }))} />
        <FilterSelect id="evento-turmas-ciclo" value={cycle} onChange={setCycle} items={[{ value: ALL, label: 'Todos os ciclos' }, ...cycles.map((c) => ({ value: c, label: c }))]} />
        <FilterSelect id="evento-turmas-segmento" value={seg} onChange={setSeg} items={[{ value: ALL, label: 'Todos os segmentos' }, ...segs.map((c) => ({ value: c, label: c }))]} />
        <ClassMultiSelect options={[...shown, ...opts.filter((o) => sel.includes(o.id) && !shown.includes(o))]} selected={sel} onChange={setSel} />
      </div>
      <p className="mt-3 text-[12px]" style={{ color: C.muted }}>Somente alunos ativos das turmas selecionadas são carregados. Alunos com pagamento registrado nunca são removidos.</p>
    </Panel>
  );
}

function ValorCell({ p, onSave }) {
  const [v, setV] = useState(p.valor ?? '');
  useEffect(() => setV(p.valor ?? ''), [p.valor]);
  const commit = () => { const cur = p.valor ?? ''; if (String(v) !== String(cur)) onSave(v === '' ? { clear_valor: true } : { valor: Number(v) }); };
  return <Input type="number" min="0" step="0.01" value={v} placeholder={String(p.valor_cobrado)} onChange={(e) => setV(e.target.value)} onBlur={commit} className="h-8 w-[96px]" disabled={p.pago > 0} data-testid={`evento-valor-${p.student_id}`} />;
}

export function TurmasAlunos(props) {
  const { data, setData, eventId } = props;
  const { list, toolbar } = useParticipantFilter(data.participants);
  const patch = async (sid, body) => {
    try { const { data: d } = await api.patch(`/eventos/${eventId}/participantes/${sid}`, body); setData(d); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Falha ao atualizar aluno.'); }
  };
  const rows = list.map((p) => (
    <TableRow key={p.student_id} data-testid={`evento-aluno-${p.student_id}`} style={p.participa ? undefined : { opacity: 0.55 }}>
      <TableCell><p className="font-semibold">{p.student_name}</p><p className="text-[11px]" style={{ color: C.muted }}>{p.responsible_name || 'sem responsável financeiro'}</p></TableCell>
      <TableCell>{p.class_name}</TableCell>
      <TableCell><Switch checked={!!p.participa} onCheckedChange={(v) => patch(p.student_id, { participa: v })} disabled={p.pago > 0 && p.participa} data-testid={`evento-participa-${p.student_id}`} /></TableCell>
      <TableCell><AuthBadge p={p} /></TableCell>
      <TableCell><ValorCell p={p} onSave={(b) => patch(p.student_id, b)} /></TableCell>
      <TableCell className="tabular-nums">{brl(p.pago)}</TableCell>
      <TableCell>{p.forma || '—'}</TableCell>
      <TableCell>{fmtDate(p.data_pagamento)}</TableCell>
      <TableCell><PayBadge p={p} /></TableCell>
    </TableRow>
  ));
  return (
    <div className="space-y-4">
      <ClassPicker {...props} />
      <ParticipantShell title={`Alunos (${list.length})`} toolbar={toolbar} testid="evento-alunos-table" rows={rows}
        head={['Aluno', 'Turma', 'Participará', 'Autorização', 'Valor', 'Pago', 'Forma', 'Data', 'Status']}
        footer="Valor vazio = valor padrão do evento. Isenção/cortesia na aba Recebimentos." />
    </div>
  );
}
